/**
 * Små delte UI-komponenter.
 */
import { useState, type ReactNode } from 'react';
import type { ChargerState } from '../lib/api.ts';

export function Badge({ state }: { state: ChargerState | null }) {
  if (!state) return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">Ukjent</span>;
  if (!state.online)
    return <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">Frakoblet nett</span>;
  const stiler: Record<string, [string, string]> = {
    LADER: ['bg-green-100 text-green-700', 'Lader'],
    VENTER: ['bg-amber-100 text-amber-700', 'Venter/pauset'],
    FULLFORT: ['bg-blue-100 text-blue-700', 'Fullført'],
    FRAKOBLET: ['bg-slate-100 text-slate-600', 'Ingen bil'],
    UKJENT: ['bg-slate-100 text-slate-500', 'Ukjent'],
  };
  const [klasse, tekst] = stiler[state.modus] ?? stiler.UKJENT;
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${klasse}`}>{tekst}</span>;
}

export function Modal({
  tittel,
  onClose,
  children,
  bred = false,
}: {
  tittel: string;
  onClose: () => void;
  children: ReactNode;
  bred?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className={`max-h-[90vh] w-full ${bred ? 'max-w-2xl' : 'max-w-lg'} overflow-y-auto rounded-xl bg-white p-6 shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">{tittel}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Lukk">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block text-sm font-medium text-slate-700">
      {label}
      {children}
    </label>
  );
}

export const inputKlasse =
  'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

export const knappPrimær =
  'rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50';

export const knappSekundær =
  'rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50';

/** Feilmelding + enkel mutasjonsstatus for skjemaer. */
export function useFeil() {
  const [feil, setFeil] = useState<string | null>(null);
  return {
    feil,
    setFeil,
    Feil: feil ? <p className="mt-2 text-sm text-red-600">{feil}</p> : null,
  };
}
